/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import type {
  FutureActionsForResourceFilterInput,
  PastActionsForResourceFilterInput,
} from "~/generated/graphql";
import {
  FutureActionsForResourceFilterByFields,
  PastActionsForResourceFilterByFields,
} from "~/generated/graphql";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { prepareLogErrorCodeFilterBy } from "~/utils/logErrorCodeFilters";
import { ResourceDetailsActionsQuery } from "../../types/ResourceDetailsActionsQuery";

export const preparePastActionsFilter = (
  queryState: ResourceDetailsActionsQuery,
  phrase?: string
): PastActionsForResourceFilterInput | undefined => {
  const resourceStatusesFilter =
    createFilterByInput<PastActionsForResourceFilterByFields>(
      PastActionsForResourceFilterByFields.Status,
      queryState.resourceStatuses ?? [],
      queryState.excludeResourceStatuses ?? false
    );

  const actionNamesFilter =
    createFilterByInput<PastActionsForResourceFilterByFields>(
      PastActionsForResourceFilterByFields.ActionName,
      queryState.actionNames ?? [],
      queryState.excludeActionNames ?? false
    );

  const actionTypesFilter =
    createFilterByInput<PastActionsForResourceFilterByFields>(
      PastActionsForResourceFilterByFields.ActionType,
      queryState.actionTypes ?? [],
      queryState.excludeActionTypes ?? false
    );

  const actionLogOutputFilter = prepareLogErrorCodeFilterBy(
    PastActionsForResourceFilterByFields.ErrorCode,
    queryState.actionLogOutputs,
    queryState.excludeActionLogOutputs
  );

  const filterBy = [
    ...resourceStatusesFilter,
    ...actionNamesFilter,
    ...actionTypesFilter,
    ...actionLogOutputFilter,
  ];

  if (filterBy.length === 0 && !phrase) {
    return undefined;
  }

  return {
    phrase: phrase || undefined,
    filterBy: filterBy.length > 0 ? filterBy : undefined,
  };
};

export const prepareFutureActionsFilter = (
  queryState: ResourceDetailsActionsQuery,
  phrase?: string
): FutureActionsForResourceFilterInput | undefined => {
  const actionNamesFilter =
    createFilterByInput<FutureActionsForResourceFilterByFields>(
      FutureActionsForResourceFilterByFields.ActionName,
      queryState.actionNames ?? [],
      queryState.excludeActionNames ?? false
    );

  const actionTypesFilter =
    createFilterByInput<FutureActionsForResourceFilterByFields>(
      FutureActionsForResourceFilterByFields.ActionType,
      queryState.actionTypes ?? [],
      queryState.excludeActionTypes ?? false
    );

  const filterBy = [...actionNamesFilter, ...actionTypesFilter];

  if (filterBy.length === 0 && !phrase) {
    return undefined;
  }

  return {
    phrase: phrase || undefined,
    filterBy: filterBy.length > 0 ? filterBy : undefined,
  };
};
